import { createSlice } from "@reduxjs/toolkit";
import { checkStorePage } from "../utils/helpers";
import { allLinks } from "../utils/helpers";
import { getUserBannerAsync } from "./request/requestSlice";

// Ensure default language is set
const DEFAULT_LANGUAGE = 'en';
const storedLanguage = localStorage.getItem("i18nextLng");
if (!storedLanguage) {
  localStorage.setItem("i18nextLng", DEFAULT_LANGUAGE);
}

const initialState = {
  isLoading: false,
  error: null,
  message: null,

  valueTab: 0,
  route: "/",

  language: storedLanguage || DEFAULT_LANGUAGE,

  pageNotFound: false,

  isOnline: true,

  notification: [],
  badgeContent: 0,

  bannerLoaded: false,
  bannerLoading: false,

  lastStorePath: null,

  screenWidth: 1024,
  mediumWidthScreen: 1024,

  // Dark mode: null = system preference, true = dark, false = light
  // Read from cookie first (shared across subdomains), fallback to localStorage
  darkMode: (() => {
    const cookieMatch = document.cookie.match(/(?:^|; )darkMode=(true|false)/);
    if (cookieMatch) return cookieMatch[1] === 'true';
    if (localStorage.getItem('darkMode') !== null) return localStorage.getItem('darkMode') === 'true';
    return null;
  })(),
};

const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload.isLoading;
    },
    setError: (state, action) => {
      state.error = action.payload.error;
    },
    setMessage: (state, action) => {
      state.message = action.payload.message;
    },
    setPageNotFound: (state, action) => {
      state.pageNotFound = action.payload;
    },
    setValueTab: (state, action) => {
      if (action.payload !== undefined) state.valueTab = action.payload;
      else {
        // when reload page only
        if (checkStorePage()) state.valueTab = 2;
        else if (
          window.location.href.includes(allLinks[10]) ||
          window.location.href.includes(allLinks[13])
        )
          state.valueTab = 4;
      }
    },
    setRoute: (state, action) => {
      state.route = action.payload;
    },
    setIsOnline: (state, action) => {
      state.isOnline = action.payload;
    },
    setNotification: (state, action) => {
      state.notification = [action.payload, ...state.notification];
    },
    setBadgeContent: (state, action) => {
      state.badgeContent = action.payload !== 0 ? action.payload : 0;
    },
    setLanguage: (state, action) => {
      state.language = action.payload;
    },
    setLastStorePath: (state, action) => {
      state.lastStorePath = action.payload;
    },
    setScreenWidth: (state, action) => {
      state.screenWidth = action.payload;
    },
    setDarkMode: (state, action) => {
      state.darkMode = action.payload;
      // Get root domain for cross-subdomain cookie sharing
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      const rootDomain = parts.length >= 2 ? '.' + parts.slice(-2).join('.') : hostname;

      if (action.payload !== null) {
        localStorage.setItem('darkMode', action.payload.toString());
        document.cookie = `darkMode=${action.payload}; path=/; domain=${rootDomain}; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
      } else {
        localStorage.removeItem('darkMode');
        document.cookie = `darkMode=; path=/; domain=${rootDomain}; max-age=0; SameSite=Lax`;
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getUserBannerAsync.pending, (state) => {
        state.bannerLoading = true;
      })
      .addCase(getUserBannerAsync.fulfilled, (state) => {
        state.bannerLoading = false;
        state.bannerLoaded = true;
      })
      .addCase(getUserBannerAsync.rejected, (state) => {
        state.bannerLoading = false;
      });
  },
});

export const {
  setLoading,
  setError,
  setMessage,
  setPageNotFound,
  setValueTab,
  setRoute,
  setIsOnline,
  setNotification,
  setBadgeContent,
  setLanguage,
  setLastStorePath,
  setScreenWidth,
  setDarkMode
} = globalSlice.actions;

export default globalSlice.reducer;
